/**
 * Configuration des labels de tourisme officiels
 * Source unique de vérité pour les labels — utilisé côté client et serveur
 *
 * Les logos sont stockés dans public/images/labels/<key>.svg
 * Un label sans logo configuré ne sera pas affiché (AC5)
 */

export const TOURISM_LABEL_KEYS = [
  'AccueilVelo',
  'GitesDeFrame',
  'Clevacances',
  'TourismeHandicap',
  'BienvenueAlaFerme',
  'Logis',
  'EcolabelEuropeen',
  'RefugeLPO',
  'ClefVerte',
  'RandoAccueil',
  'DestinationExcellence',
  'ValeursPNR',
  'FleursDeSoleil',
  'VignoblesEtDecouvertes',
  'AccueilPaysan',
] as const

/**
 * Bitfield pour les 4 types de handicap du label Tourisme & Handicap.
 * Valeurs combinables par OR binaire.
 * Ex : Auditif + Moteur = 1 | 4 = 5
 */
export const TOURISME_HANDICAP_BITS = {
  Auditif: 1,
  Mental: 2,
  Moteur: 4,
  Visuel: 8,
} as const

export type TourismeHandicapBit = (typeof TOURISME_HANDICAP_BITS)[keyof typeof TOURISME_HANDICAP_BITS]

/** Retourne le nom de fichier du logo pour une combinaison de handicaps (bitfield) */
export function getTourismeHandicapLogoKey(bits: number): string {
  const parts: string[] = []
  if (bits & TOURISME_HANDICAP_BITS.Auditif) parts.push('A')
  if (bits & TOURISME_HANDICAP_BITS.Mental) parts.push('Me')
  if (bits & TOURISME_HANDICAP_BITS.Moteur) parts.push('Mo')
  if (bits & TOURISME_HANDICAP_BITS.Visuel) parts.push('V')
  return `TourismeHandicap_${parts.join('-')}`
}

/** Retourne le label textuel d'une combinaison de handicaps (bitfield) */
export function getTourismeHandicapLabel(bits: number): string {
  const parts: string[] = []
  if (bits & TOURISME_HANDICAP_BITS.Auditif) parts.push('Auditif')
  if (bits & TOURISME_HANDICAP_BITS.Mental) parts.push('Mental')
  if (bits & TOURISME_HANDICAP_BITS.Moteur) parts.push('Moteur')
  if (bits & TOURISME_HANDICAP_BITS.Visuel) parts.push('Visuel')
  return parts.join(' + ')
}

/** Vérifie qu'un bitfield représente une combinaison valide (2 à 4 handicaps) */
export function isValidTourismeHandicapBits(bits: number): boolean {
  const count = [1, 2, 4, 8].filter(b => bits & b).length
  return count >= 2 && count <= 4
}

export type TourismLabelKey = (typeof TOURISM_LABEL_KEYS)[number]

/** Définition d'un sous-niveau pour un label (ex: épis, clés, cheminées) */
export interface TourismLabelSublevel {
  /** Valeur numérique du niveau */
  value: number
  /** Libellé affiché (ex: "1 épi", "★★★") */
  label: string
}

export interface TourismLabelConfig {
  key: TourismLabelKey
  /** Nom court affiché sur le badge */
  name: string
  /** Description affichée dans le tooltip */
  description: string
  /** Organisme certificateur */
  certifier: string
  /** URL vers le site de l'organisme (optionnel) */
  certifierUrl?: string
  /** Lien vers le profil de l'établissement chez l'organisme (optionnel, paramétrable) */
  profileUrl?: string
  /** Type Schema.org à utiliser pour ce label */
  schemaOrgType: 'award' | 'hasCredential'
  /**
   * Formats de logo disponibles, par ordre de priorité.
   * Par défaut : ['svg', 'webp', 'png']. Ne lister que les fichiers réellement présents.
   */
  logoFormats?: Array<'svg' | 'webp' | 'png'>
  /**
   * Sous-niveaux disponibles pour ce label (optionnel).
   * Si défini, le sélecteur affichera un menu déroulant de niveau.
   * Symbole affiché sur le badge public (ex: "épi", "clé", "cheminée")
   */
  sublevelConfig?: {
    symbol: string
    levels: TourismLabelSublevel[]
  }
}

/**
 * Configuration complète de chaque label officiel
 */
export const TOURISM_LABELS: Record<TourismLabelKey, TourismLabelConfig> = {
  AccueilVelo: {
    key: 'AccueilVelo',
    name: 'Accueil Vélo',
    description: 'Hébergement labellisé pour l\'accueil des cyclotouristes : stationnement sécurisé vélos, trousse à outils, guide des itinéraires.',
    certifier: 'France Vélo Tourisme',
    certifierUrl: 'https://www.france-velo-tourisme.com',
    schemaOrgType: 'award',
  },
  GitesDeFrame: {
    key: 'GitesDeFrame',
    name: 'Gîtes de France',
    description: 'Label qualité tourisme du réseau Gîtes de France, garantissant confort, authenticité et accueil personnalisé.',
    certifier: 'Gîtes de France',
    certifierUrl: 'https://www.gites-de-france.com',
    schemaOrgType: 'hasCredential',
    sublevelConfig: {
      symbol: 'épi',
      levels: [
        { value: 1, label: '1 épi' },
        { value: 2, label: '2 épis' },
        { value: 3, label: '3 épis' },
        { value: 4, label: '4 épis' },
      ],
    },
  },
  Clevacances: {
    key: 'Clevacances',
    name: 'Clévacances',
    description: 'Label national de qualité pour les locations et chambres d\'hôtes, classé de 1 à 5 clés.',
    certifier: 'Clévacances',
    certifierUrl: 'https://www.clevacances.com',
    schemaOrgType: 'hasCredential',
    sublevelConfig: {
      symbol: 'clé',
      levels: [
        { value: 1, label: '1 clé' },
        { value: 2, label: '2 clés' },
        { value: 3, label: '3 clés' },
        { value: 4, label: '4 clés' },
        { value: 5, label: '5 clés' },
      ],
    },
  },
  TourismeHandicap: {
    key: 'TourismeHandicap',
    name: 'Tourisme & Handicap',
    description: 'L\'établissement est accessible et adapté aux personnes en situation de handicap. Le label couvre 2 à 4 types de handicap : Auditif, Mental, Moteur, Visuel.',
    certifier: 'Tourisme & Handicaps',
    certifierUrl: 'https://www.tourisme-handicaps.org',
    schemaOrgType: 'award',
    logoFormats: [],
  },
  BienvenueAlaFerme: {
    key: 'BienvenueAlaFerme',
    name: 'Bienvenue à la Ferme',
    description: 'Réseau d\'agriculteurs qui ouvrent leur ferme au public : chambres d\'hôtes à la ferme, produits locaux, authenticité garantie.',
    certifier: 'Bienvenue à la Ferme',
    certifierUrl: 'https://www.bienvenue-a-la-ferme.com',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
  Logis: {
    key: 'Logis',
    name: 'Logis Hôtels',
    description: 'Label de la première chaîne hôtelière française indépendante, synonyme d\'authenticité et d\'ancrage local.',
    certifier: 'Logis Hôtels',
    certifierUrl: 'https://www.logishotels.com',
    schemaOrgType: 'hasCredential',
    logoFormats: ['webp'],
    sublevelConfig: {
      symbol: 'cheminée',
      levels: [
        { value: 1, label: '1 cheminée' },
        { value: 2, label: '2 cheminées' },
        { value: 3, label: '3 cheminées' },
        { value: 4, label: '4 cheminées' },
        { value: 5, label: '5 cheminées' },
      ],
    },
  },
  EcolabelEuropeen: {
    key: 'EcolabelEuropeen',
    name: 'Écolabel Européen',
    description: 'Certification environnementale européenne officielle récompensant les hébergements les plus respectueux de l\'environnement.',
    certifier: 'Commission Européenne',
    certifierUrl: 'https://ecolabel.ec.europa.eu',
    schemaOrgType: 'hasCredential',
  },
  RefugeLPO: {
    key: 'RefugeLPO',
    name: 'Refuge LPO',
    description: 'L\'établissement s\'engage pour la biodiversité locale et l\'accueil de la faune sauvage dans son jardin.',
    certifier: 'Ligue pour la Protection des Oiseaux',
    certifierUrl: 'https://www.lpo.fr',
    schemaOrgType: 'award',
  },
  ClefVerte: {
    key: 'ClefVerte',
    name: 'Clef Verte',
    description: 'Premier label international d\'écotourisme pour les hébergements s\'engageant en faveur de l\'environnement.',
    certifier: 'La Clef Verte',
    certifierUrl: 'https://www.laclefverte.org',
    schemaOrgType: 'award',
    logoFormats: ['webp'],
  },
  RandoAccueil: {
    key: 'RandoAccueil',
    name: 'Rando Accueil',
    description: 'Label national distinguant les hébergements situés à proximité immédiate de sentiers de randonnée, avec services dédiés aux randonneurs itinérants.',
    certifier: 'Réseau Rando Accueil',
    certifierUrl: 'https://www.rando-accueil.com',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
  DestinationExcellence: {
    key: 'DestinationExcellence',
    name: 'Destination d\'Excellence',
    description: 'Label d\'État français (remplace Qualité Tourisme) certifiant la qualité d\'accueil et l\'éco-responsabilité de l\'hébergement. Délivré par Atout France.',
    certifier: 'Atout France',
    certifierUrl: 'https://www.atout-france.fr/fr/destination-dexcellence',
    schemaOrgType: 'hasCredential',
  },
  ValeursPNR: {
    key: 'ValeursPNR',
    name: 'Valeurs Parc naturel régional',
    description: 'Marque collective des Parcs naturels régionaux de France, reconnaissant les hébergements engagés pour la préservation du patrimoine naturel, l\'ancrage territorial et une dimension humaine authentique.',
    certifier: 'Fédération des Parcs naturels régionaux de France',
    certifierUrl: 'https://www.parcs-naturels-regionaux.fr/les-enjeux/economie/valeurs-parc-naturel-regional/valeurs-parc-naturel-regional-la-marque',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
  FleursDeSoleil: {
    key: 'FleursDeSoleil',
    name: 'Fleurs de Soleil',
    description: 'Réseau national de chambres d\'hôtes de charme, sélectionnées pour la qualité de leur accueil, l\'authenticité du lieu et le partage d\'un moment convivial avec les hôtes.',
    certifier: 'Fleurs de Soleil',
    certifierUrl: 'https://www.fleursdesoleil.fr',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
  VignoblesEtDecouvertes: {
    key: 'VignoblesEtDecouvertes',
    name: 'Vignobles & Découvertes',
    description: 'Label d\'État créé par Atout France, distinguant les destinations viticoles qui proposent une offre œnotouristique structurée autour de la vigne et du vin. L\'hébergement est partenaire d\'une destination labellisée.',
    // Certifieur = la Fédération, qui anime le réseau et édite le portail vers lequel
    // pointe `certifierUrl`. Nommer « Atout France » ici produirait un nœud Organization
    // homonyme de celui de DestinationExcellence, avec une URL divergente.
    certifier: 'Fédération Nationale Vignobles & Découvertes',
    certifierUrl: 'https://www.vignobles-decouvertes.com',
    schemaOrgType: 'award',
    logoFormats: ['webp'],
  },
  AccueilPaysan: {
    key: 'AccueilPaysan',
    name: 'Accueil Paysan',
    description: 'Réseau de paysans qui accueillent en chambres paysannes sur leur ferme : adhésion à une charte éthique d\'agriculture durable, d\'accueil chaleureux et de partage de la vie paysanne.',
    certifier: 'Fédération nationale Accueil Paysan',
    certifierUrl: 'https://www.accueil-paysan.com/fr/',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
}

/**
 * Groupes d'affichage des labels dans le sélecteur propriétaire et la page admin
 * des URLs de profil. Source unique — ne pas redéclarer localement dans les vues.
 *
 * Invariant : chaque clé de TOURISM_LABEL_KEYS appartient à exactement un groupe
 * (verrouillé par tourism-labels.spec.ts). Un label absent d'un groupe serait
 * invisible dans les deux écrans, sans aucun signal.
 */
export const TOURISM_LABEL_GROUPS = [
  {
    title: 'Hébergement classé',
    keys: ['GitesDeFrame', 'Clevacances', 'Logis', 'FleursDeSoleil'],
  },
  {
    title: 'Tourisme durable',
    keys: ['EcolabelEuropeen', 'ClefVerte', 'RefugeLPO', 'ValeursPNR'],
  },
  {
    title: 'Tourisme actif',
    keys: ['AccueilVelo', 'RandoAccueil', 'BienvenueAlaFerme', 'AccueilPaysan'],
  },
  {
    title: 'Label qualité',
    keys: ['DestinationExcellence', 'VignoblesEtDecouvertes'],
  },
  {
    title: 'Accessibilité',
    keys: ['TourismeHandicap'],
  },
] as const satisfies ReadonlyArray<{ title: string, keys: readonly TourismLabelKey[] }>

/**
 * Clés dotées d'une UI dédiée dans le sélecteur propriétaire (bitfield de handicaps)
 * et donc exclues de son rendu par groupes, pour éviter un doublon.
 * La page admin des URLs, elle, les affiche dans leur groupe.
 */
export const SELF_MANAGED_TOURISM_LABEL_KEYS: readonly TourismLabelKey[] = ['TourismeHandicap']

/**
 * Groupes tels que les rend le sélecteur propriétaire : sans les clés à UI dédiée,
 * et sans les groupes que ce retrait a vidés.
 *
 * Vit ici et non dans le composant pour que le test l'exerce réellement, au lieu
 * d'en recopier le calcul — la duplication de logique se paierait comme celle des
 * données qu'on vient de supprimer.
 */
export function getOwnerTourismLabelGroups(): Array<{ title: string, keys: TourismLabelKey[] }> {
  return TOURISM_LABEL_GROUPS
    .map(group => ({
      title: group.title,
      keys: group.keys.filter(key => !SELF_MANAGED_TOURISM_LABEL_KEYS.includes(key)) as TourismLabelKey[],
    }))
    .filter(group => group.keys.length > 0)
}

/** Titre du groupe contenant une clé donnée — évite de recopier un intitulé dans un template. */
export function getTourismLabelGroupTitle(key: TourismLabelKey): string | undefined {
  return TOURISM_LABEL_GROUPS.find(group => group.keys.some(k => k === key))?.title
}

/**
 * Retourne la config d'un label à partir de sa clé.
 * Retourne undefined si la clé est invalide.
 */
export function getTourismLabelConfig(key: string): TourismLabelConfig | undefined {
  return TOURISM_LABELS[key as TourismLabelKey]
}

/**
 * Vérifie si une clé est un label de tourisme valide
 */
export function isValidTourismLabelKey(key: string): key is TourismLabelKey {
  return TOURISM_LABEL_KEYS.includes(key as TourismLabelKey)
}

/**
 * Chemins candidats vers le logo d'un label, par ordre de priorité.
 * Utilise `logoFormats` si défini sur le label, sinon ['svg', 'webp', 'png'].
 * Pour TourismeHandicap, utiliser getTourismeHandicapLogoCandidates(bits) à la place.
 */
export function getTourismLabelLogoCandidates(key: TourismLabelKey): string[] {
  const formats = TOURISM_LABELS[key].logoFormats ?? ['svg', 'webp', 'png']
  return formats.map(ext => `/images/labels/${key}.${ext}`)
}

/**
 * Chemins candidats pour le logo Tourisme & Handicap selon le bitfield de handicaps.
 * Ex: bits=5 (Auditif+Moteur) → ['/images/labels/TourismeHandicap_A-Mo.png']
 */
export function getTourismeHandicapLogoCandidates(bits: number): string[] {
  if (!isValidTourismeHandicapBits(bits)) return []
  return [`/images/labels/${getTourismeHandicapLogoKey(bits)}.png`]
}

/**
 * @deprecated Utiliser getTourismLabelLogoCandidates pour supporter SVG et PNG
 */
export function getTourismLabelLogoPath(key: TourismLabelKey): string {
  return `/images/labels/${key}.svg`
}

/**
 * Retourne le libellé du sous-niveau pour un label donné.
 * Ex: formatSublevel('GitesDeFrame', 3) → "3 épis"
 */
export function formatSublevel(key: string, level: number): string {
  const config = getTourismLabelConfig(key)
  if (!config?.sublevelConfig) return String(level)
  return config.sublevelConfig.levels.find(l => l.value === level)?.label ?? String(level)
}
